import { useCallback, useState } from "react";

import { useHistory } from "react-router-dom";
import { type CloudAnalyticsModelMetricModel } from "@doitintl/cmp-models";
import { modelServerTimestamp, type ModelTimestamp } from "@doitintl/models-any";
import { Typography } from "@mui/material";

import { metricTxt } from "../../../assets/texts/CloudAnalytics";
import ResourceEditDialog from "../../../Components/EditableResourceHeader/ResourceEditDialog";
import useRouteMatchURL from "../../../Components/hooks/useRouteMatchURL";
import {
  useErrorSnackbar,
  useInfoSnackbar,
  useSuccessSnackbar,
} from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { ThreeDotsMenu } from "../../../Components/ThreeDotsMenu";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useIsFeatureEntitled } from "../../../Context/TierProvider";
import { consoleErrorWithSentry } from "../../../utils";
import mixpanel from "../../../utils/mixpanel";
import { copyMetric, saveMetricData } from "./db";
import { MetricsMenuOptions } from "./types";

export const useCreateMetricHandler = ({
  mixpanelEventName,
  baseUrl,
}: {
  mixpanelEventName: string;
  baseUrl: string;
}) => {
  const history = useHistory();
  const routeMatchURL = useRouteMatchURL();

  return useCallback(() => {
    mixpanel.track(mixpanelEventName);
    history.push(`${baseUrl}/create`, { prevPage: routeMatchURL });
  }, [baseUrl, history, mixpanelEventName, routeMatchURL]);
};

export const useDuplicateMetric = ({ row }) => {
  const infoSnackbar = useInfoSnackbar(3);
  const { customer } = useCustomerContext();
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const errorSnackbar = useErrorSnackbar();

  return useCallback(async () => {
    try {
      await copyMetric(customer.ref, currentUser.email, row.data);
      infoSnackbar(metricTxt.DUPLICATE_SUCCESSFUL);
    } catch (e) {
      errorSnackbar(metricTxt.CREATE_METRIC_ERROR);
      consoleErrorWithSentry(e);
    }
  }, [currentUser.email, customer.ref, errorSnackbar, infoSnackbar, row.data]);
};

export const useRenameMetric = ({ row }) => {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const successSnackbar = useSuccessSnackbar();
  const errorSnackbar = useErrorSnackbar();

  const handleSave = useCallback(
    async (values: { name: string; description?: string }) => {
      try {
        const metricData: Partial<CloudAnalyticsModelMetricModel> = {
          name: values.name,
          description: values.description ?? row.data.description,
          timeModified: modelServerTimestamp() as ModelTimestamp,
        };
        setIsRenameDialogOpen(false);
        await saveMetricData({ ...row.data, ...metricData }, row.ref.id);
        successSnackbar(metricTxt.METRIC_SAVE_SUCCESS);
      } catch (e) {
        errorSnackbar(metricTxt.METRIC_SAVE_ERROR);
        consoleErrorWithSentry(e);
      }
    },
    [row, errorSnackbar, successSnackbar]
  );

  return {
    isRenameDialogOpen,
    openRenameDialog: () => {
      setIsRenameDialogOpen(true);
    },
    closeRenameDialog: () => {
      setIsRenameDialogOpen(false);
    },
    handleSave,
  };
};

export const useThreeDotsMetricsMenu = ({ row }) => {
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const isEntitledCalculatedMetrics = useIsFeatureEntitled("analytics:calculatedMetrics");
  const duplicateMetric = useDuplicateMetric({ row });
  const { openRenameDialog, isRenameDialogOpen, closeRenameDialog, handleSave } = useRenameMetric({ row });

  return (
    <>
      <ThreeDotsMenu
        closeAfterSelect
        options={[
          {
            label: <Typography>{MetricsMenuOptions.DUPLICATE}</Typography>,
            action: duplicateMetric,
            key: MetricsMenuOptions.DUPLICATE,
          },
          ...(!!currentUser && currentUser.email === row?.data?.owner
            ? [
                {
                  label: <Typography>{MetricsMenuOptions.RENAME}</Typography>,
                  action: openRenameDialog,
                  key: MetricsMenuOptions.RENAME,
                },
              ]
            : []),
        ]}
        disabled={!isEntitledCalculatedMetrics}
      />
      {isRenameDialogOpen && (
        <ResourceEditDialog
          open={isRenameDialogOpen}
          onClose={closeRenameDialog}
          onSave={handleSave}
          initialValues={{
            name: row.data.name,
            description: row.data.description,
          }}
          resourceName="metric"
        />
      )}
    </>
  );
};
